/* eslint-disable react-hooks/exhaustive-deps */
import { useReducer, useMemo, useCallback, memo, useEffect } from 'react';
import MultimediaContext from './MultimediaContext';
import initialMultimediaState from './initialMultimediaState';
import userReducer from 'contexts/multimedia/reducer/multimedia.reducer';
import * as action from 'contexts/multimedia/reducer/multimedia.actions';
import { useAuth0 } from '@auth0/auth0-react';
import { useTranslation } from 'react-i18next';
import {
  IInitialMultimediaState,
  MultimediaTypes,
  TCreateMultimedia,
  TMediaState,
} from 'interfaces/multimedia.interface';
import { abortCatalogController } from 'utils/abortController';
import * as MTypes from './reducer/multimedia.types';
import { IChildrenProps } from 'interfaces/general.interface';
import useContextCatalog from 'hooks/contexts/useContextCatalog';
import { IfindMultimediaAPIItemResponse } from 'api/endpoints/multimedia/multimedia.response.interface';
import useContextCompany from 'hooks/contexts/useContextCompany';

function MultimediaProvider(props: IChildrenProps) {
  const [multimediaState, dispatch] = useReducer(userReducer, initialMultimediaState);
  const multimedia = multimediaState as IInitialMultimediaState;
  const { getAccessTokenSilently } = useAuth0();
  const { company } = useContextCompany();
  const { productAttributes } = useContextCatalog();
  const { t: translate } = useTranslation();

  useEffect(() => {
    if (!company?.brand?.id) return;
    dispatch({ type: MTypes.RESET_MEDIA });
  }, [company?.brand?.id]);

  const getMedia = useCallback(
    async (type: MultimediaTypes) => {
      if (!company?.brand?.id) return false;
      const signal = await abortCatalogController();
      const token = await getAccessTokenSilently();
      return await action.getMediaAction(
        dispatch,
        multimedia[type as keyof typeof multimedia] as TMediaState,
        company.brand?.id,
        type,
        signal,
        translate,
        token
      );
    },
    [company?.brand?.id, multimedia]
  );

  const getMoreMedia = useCallback(
    async (type: MultimediaTypes) => {
      if (!company?.brand?.id) return false;
      const signal = await abortCatalogController();
      const token = await getAccessTokenSilently();
      return await action.getMoreMediaAction(
        dispatch,
        multimedia[type as keyof typeof multimedia] as TMediaState,
        company.brand?.id,
        type,
        signal,
        translate,
        token
      );
    },
    [company?.brand?.id, multimedia]
  );

  const searchMedia = useCallback(
    async (query: string, type: MultimediaTypes) => {
      const signal = await abortCatalogController();
      const token = await getAccessTokenSilently();
      return await action.searchMediaAction(
        dispatch,
        multimedia[type as keyof typeof multimedia] as TMediaState,
        query,
        type,
        signal,
        translate,
        token
      );
    },
    [multimedia]
  );

  const createMultimedia = useCallback(
    async ({
      body,
      media,
      videoThumbnailMedia,
      setCurrentStep,
      type,
      products,
    }: TCreateMultimedia) => {
      if (!company || !productAttributes) return false;
      const token = await getAccessTokenSilently();
      return (await action.createMultimediaAction(
        dispatch,
        company,
        type,
        body,
        media,
        videoThumbnailMedia,
        setCurrentStep,
        productAttributes,
        products,
        translate,
        token
      )) as boolean;
    },
    [company, productAttributes]
  );

  const updateMultimedia = useCallback(
    async ({ body, type }: { body: any; type: MultimediaTypes }) => {
      if (!productAttributes) return false;
      const token = await getAccessTokenSilently();
      return (await action.updateMultimediaAction(
        dispatch,
        multimedia,
        productAttributes,
        body,
        type,
        translate,
        token
      )) as boolean;
    },
    [multimedia, productAttributes]
  );

  const setItemSelected = useCallback((item: IfindMultimediaAPIItemResponse) => {
    dispatch({ type: MTypes.SET_ITEM_SELECTED, payload: item });
  }, []);

  const setIsOpenEditModal = useCallback((showEditModal: boolean) => {
    dispatch({ type: MTypes.SET_IS_OPEN_EDIT_MODAL, payload: showEditModal });
  }, []);

  const removeMultimedia = useCallback(
    async ({
      mediaId,
      multimediaType,
    }: {
      mediaId: string;
      multimediaType: MultimediaTypes;
    }) => {
      const token = await getAccessTokenSilently();
      return (await action.removeMultimediaAction(
        dispatch,
        mediaId,
        multimediaType,
        translate,
        token
      )) as boolean;
    },
    []
  );

  const memoProvider = useMemo(
    () => ({
      ...multimedia,
      getMedia,
      getMoreMedia,
      setItemSelected,
      removeMultimedia,
      searchMedia,
      createMultimedia,
      updateMultimedia,
      setIsOpenEditModal,
    }),
    [
      multimedia,
      getMedia,
      getMoreMedia,
      setItemSelected,
      removeMultimedia,
      searchMedia,
      createMultimedia,
      updateMultimedia,
      setIsOpenEditModal,
    ]
  );

  return (
    <MultimediaContext.Provider value={memoProvider}>
      {props.children}
    </MultimediaContext.Provider>
  );
}

export default memo(MultimediaProvider);
